import { useCallback, useState } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Dialog } from '@opendoor/atoms/dialog';
import { Text } from '@opendoor/atoms/text';
import { Toasty } from '@opendoor/atoms/toast';
import { cn } from '@opendoor/atoms/utils';
import { IconMail, IconMessage, IconX } from '@tabler/icons-react';
import { getShareableLink } from 'components/helpers/links';
import { useTrackProperty } from 'components/marketplace/helpers/useTrackProperty';
import { CopyLinkToast } from 'components/marketplace/shared/CopyLinkToast';

import { useObservability } from '../../../helpers/observability';
import { formatStreetAddress } from '../helpers/addressHelpers';
import type { MarketplaceProperty } from './types';

function getShareableContent(slug: string, addressToken: string) {
  return `Check out this home for sale on Opendoor!\n\n${getShareableLink(slug, addressToken)}`;
}

function getEmailLink(slug: string, addressToken: string) {
  return `mailto:?body=${encodeURIComponent(getShareableContent(slug, addressToken))}`;
}

function getTextMessageLink(slug: string, addressToken: string) {
  return `sms:?&body=${encodeURIComponent(getShareableContent(slug, addressToken))}`;
}

const TOAST_DURATION_MS = 1_000;
const SHARE_ACTION_CLASS =
  'h-auto w-full justify-between rounded-none border-x-0 border-t-0 border-b border-od-border-secondary bg-transparent px-0 py-6';

export type ShareableProperty = Pick<
  MarketplaceProperty,
  'addressToken' | 'slug' | 'street' | 'unitNumber' | 'unitType'
>;

export interface ShareModalProps {
  property: ShareableProperty;
  displayAddress?: string | null;
  isOpen: boolean;
  onClose: () => void;
  trackingSource?: 'gallery' | 'pdp';
}

const copyToClipboard = (text: string) => {
  if (navigator.clipboard) {
    void navigator.clipboard.writeText(text);
  }
};

export const ShareModal = ({
  property,
  displayAddress,
  isOpen,
  onClose,
  trackingSource = 'pdp',
}: ShareModalProps) => {
  const [isToastOpen, setIsToastOpen] = useState(false);
  const { trackProperty } = useTrackProperty();
  const { trackJourneyEvent } = useObservability();
  const formattedStreet =
    displayAddress ?? formatStreetAddress(property.street, property.unitType, property.unitNumber);
  const handleOnShare = useCallback(() => {
    trackProperty({
      featureName: 'property-share',
      label: `marketplace-${trackingSource}-copy`,
      options: { addressToken: property.addressToken },
    });
    if (property.addressToken) {
      trackJourneyEvent('marketplaceProperty', 'share', {
        addressUuid: property.addressToken,
        source: 'copy',
      });
    }
    copyToClipboard(getShareableLink(property.slug, property.addressToken));
    setIsToastOpen(true);
    onClose();
  }, [
    onClose,
    property.addressToken,
    property.slug,
    trackJourneyEvent,
    trackProperty,
    trackingSource,
  ]);

  const handleSms = useCallback(() => {
    trackProperty({
      featureName: 'property-share',
      label: `marketplace-${trackingSource}-sms`,
      options: { addressToken: property.addressToken },
    });
    window.open(getTextMessageLink(property.slug, property.addressToken), '_self');
    trackJourneyEvent('marketplaceProperty', 'share', {
      addressUuid: property.addressToken,
      source: 'sms',
    });
  }, [property.addressToken, property.slug, trackJourneyEvent, trackProperty, trackingSource]);

  const handleEmail = useCallback(() => {
    trackProperty({
      featureName: 'property-share',
      label: `marketplace-${trackingSource}-email`,
      options: { addressToken: property.addressToken },
    });
    trackJourneyEvent('marketplaceProperty', 'share', {
      addressUuid: property.addressToken,
      source: 'email',
    });
    window.open(getEmailLink(property.slug, property.addressToken), '_self');
  }, [property.addressToken, property.slug, trackJourneyEvent, trackProperty, trackingSource]);

  const closeToast = useCallback(() => {
    setIsToastOpen(false);
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
    },
    [onClose],
  );

  return (
    <Toasty>
      <Dialog.Root role="dialog" open={isOpen} onOpenChange={handleOpenChange}>
        <Dialog hideCloseButton>
          <Dialog.Title className="sr-only">Share</Dialog.Title>
          <Col className="w-full md:w-75">
            <Col className="pb-6">
              <Row className="items-center justify-between">
                <Text as="p" className="text-subheader-sm">
                  Share
                </Text>
                <Button
                  variant="secondary"
                  aria-label="Close share dialog"
                  analyticsName="close-share-modal"
                  onClick={onClose}
                  className="size-9 rounded-full border-od-border-tertiary p-0"
                >
                  <IconX size={24} />
                </Button>
              </Row>
              <Text as="p" className="pt-1 text-body-sm text-od-content-secondary">
                {formattedStreet}
              </Text>
            </Col>
            <Button
              variant="secondary"
              onClick={handleOnShare}
              analyticsName="property-share-copy"
              aria-label="Copy to clipboard"
              className={SHARE_ACTION_CLASS}
            >
              <Text as="span" className="text-body-sm">
                Copy
              </Text>
              <ContentCopyIcon size={16} />
            </Button>
            <Button
              variant="secondary"
              onClick={handleSms}
              analyticsName="property-share-sms"
              aria-label="Share by SMS"
              className={SHARE_ACTION_CLASS}
            >
              <Text as="span" className="text-body-sm">
                SMS
              </Text>
              <IconMessage size={16} />
            </Button>
            <Button
              variant="secondary"
              onClick={handleEmail}
              analyticsName="property-share-email"
              aria-label="Share by email"
              className={cn(SHARE_ACTION_CLASS, 'border-b-0')}
            >
              <Text as="span" className="text-body-sm">
                Email
              </Text>
              <IconMail size={16} />
            </Button>
          </Col>
        </Dialog>
      </Dialog.Root>
      <CopyLinkToast
        open={isToastOpen}
        message="Link copied to clipboard"
        onShown={closeToast}
        durationMs={TOAST_DURATION_MS}
      />
    </Toasty>
  );
};

function ContentCopyIcon({ size = 24 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <mask id="path-1-inside-1_3070_193338" fill="white">
        <rect x="0.5" y="3.5" width="12.5" height="16.25" rx="1.25" />
      </mask>
      <rect
        x="0.5"
        y="3.5"
        width="12.5"
        height="16.25"
        rx="1.25"
        stroke="black"
        strokeWidth="3.75"
        mask="url(#path-1-inside-1_3070_193338)"
      />
      <path
        d="M4.25 1H14.25C14.9404 1 15.5 1.55964 15.5 2.25V16"
        stroke="black"
        strokeWidth="1.875"
      />
    </svg>
  );
}
